/**
 * Single source of truth for colour, shared by the mobile app and the web app.
 * Components must not contain hex literals — see mobile/docs/design-language.md §1.
 *
 * This file moved here from mobile/src/constants/palette.ts when the web adopted
 * the same language; mobile re-exports it so nothing there had to change. Values
 * are unchanged and are enforced by the mobile legibility suite — treat them as
 * fixed points, and derive rather than edit (see surfaces.ts).
 */
export const palette = {
  /** Page ground, and the colour the bottom scrim resolves to. */
  page: '#E8EDF6',
  /** "Bone" — the paper the generated line art sits on, and the blank phone screen. */
  ground: '#F1EEE7',
  /** Primary text. */
  ink: '#17203A',
  /** Marks the current position and nothing else. Graphics only — the active dot. */
  accent: '#3A6CA8',
  /**
   * The same hue as `accent`, darkened for use as small text. `accent` itself is only
   * 4.6:1 on the page colour and drops below AA once the scrim is anything less than
   * fully opaque, which it is where the eyebrow sits. This clears 4.5:1 against the
   * worst case behind it. The two are close enough to read as one colour.
   */
  accentInk: '#2F5C93',
  /** Primary CTA fill. */
  button: '#171F33',
  buttonLabel: '#F7F9FC',
  /** Tertiary text (legal) only. */
  muted: '#5A6377',
  /** Device bezel in the phone mockup. */
  bezel: '#0E1016',
  /** Inactive pagination dot. Decorative, carries no meaning on its own. */
  dotIdle: '#B9C3D6',
  /**
   * Darkest strand the glacier ramp can produce. NOT a UI colour — legibility tests
   * composite text against this to get the worst case behind the scrim.
   */
  artInkDarkest: '#16304F',
  /**
   * Ground for the brand mark's app tile (favicon, app icon, avatar). A step
   * darker than `button` so the mark's light crescent separates further than it
   * would on the UI's own dark surfaces. Not a UI surface — see logo.ts.
   */
  brandTile: '#101B33',
  /**
   * `accent`, lightened for the brand mark when it sits on `brandTile`. The
   * accent itself only reaches 3.17:1 there, which leaves the two crescents
   * reading as one mass; this clears 7.9:1. Pinned by logo.test.ts.
   */
  accentOnDark: '#8FB4DF',
} as const;

export type PaletteToken = keyof typeof palette;
